import { NOTIFICATION_CHANNEL } from "@nlc/shared-types";
import { SuprSend } from "@suprsend/react";
import { useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/button";
import { Modal } from "@/components/modal";
import { env } from "@/config/env";
import { notificationsKeys } from "@/hooks/queryKeys";
import { useUser } from "@/hooks/useUser";

import { useConnectChannel } from "../../hooks/useConnectChannel";
import { useDisconnectChannel } from "../../hooks/useDisconnectChannel";
import { useGetSuprSendUser } from "../../hooks/useGetSuprSendUser";
import { prefixes } from "../utils/phonePrefixes";
import { showToast, ToastTypeEnum } from "../utils/showToast";
import ConnectButton from "./ConnectButton";
import DisconnectButton from "./DisconnectButton";
import { Loading } from "./Loading";
import { PhoneInput } from "./PhoneInput";

export function SMSConfig({ isDisabled }: { isDisabled: boolean }) {
  const suprSendClient = new SuprSend(env.VITE_SUPRSEND_PUBLIC_KEY);
  const [openModal, setOpenModal] = useState(false);
  const onClose = () => setOpenModal(false);
  const { data: user } = useGetSuprSendUser();
  const { data } = useUser();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const { mutate: connectChannel } = useConnectChannel();
  const { mutate: disconnectChannel } = useDisconnectChannel();

  const maskedPhone = () => {
    if (user?.$sms) {
      const phone = user.$sms[0]?.value;
      const lastFour = phone?.slice(-4);

      if (!phone) return;

      return "*".repeat(phone.length! - 4) + lastFour;
    }
  };

  const disconnectSMS = async () => {
    const smsNumber = user?.$sms?.[0]?.value;
    const email = data?.user.email;

    if (!smsNumber || !email) return;

    setLoading(true);

    await suprSendClient.identify(email);
    await suprSendClient.user.removeSms(smsNumber);

    await queryClient.invalidateQueries({
      queryKey: notificationsKeys.user(email),
    });
    disconnectChannel(NOTIFICATION_CHANNEL.SMS);
    setLoading(false);
  };

  return (
    <div className="flex">
      <ModalSMS
        onClose={onClose}
        onConnect={() => connectChannel(NOTIFICATION_CHANNEL.SMS)}
        openModal={openModal}
      />
      {user?.$sms?.length ? (
        <DisconnectButton
          isDisabled={isDisabled}
          loading={loading}
          onPress={disconnectSMS}
        />
      ) : null}
      <ConnectButton
        content={user?.$sms ? maskedPhone() : undefined}
        isDisabled={isDisabled}
        onPress={() => setOpenModal(true)}
      />
    </div>
  );
}

function ModalSMS({
  onClose,
  onConnect,
  openModal,
}: {
  onClose: () => void;
  onConnect: () => void;
  openModal: boolean;
}) {
  const [prefix, setPrefix] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const { data: user } = useGetSuprSendUser();
  const { data } = useUser();
  const suprSendClient = new SuprSend(env.VITE_SUPRSEND_PUBLIC_KEY);
  const prefixData = prefixes();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (openModal && user?.$sms?.[0]?.value) {
      const fullNumber = user.$sms[0].value;

      const foundPrefix = prefixData.find((item) =>
        fullNumber.startsWith(item.id),
      );

      if (foundPrefix) {
        const prefixFormated = `${foundPrefix.name}-${foundPrefix.id}`;

        setPrefix(prefixFormated);
        setPhone(fullNumber.replace(foundPrefix.id, ""));
      } else {
        setPrefix("");
        setPhone(fullNumber);
      }
    } else if (!openModal) {
      setPrefix("");
      setPhone("");
    }
  }, [user, openModal]);

  const handleAddSMS = async () => {
    if (user?.$email) {
      setLoading(true);

      await suprSendClient.identify(data?.user.email);

      const smsValue = user?.$sms?.[0]?.value;

      if (smsValue) {
        await suprSendClient.user.removeSms(smsValue);
      }
      await suprSendClient.user.addSms(
        `${prefix.replace(/^.*?\+.*?\+/, "+")}${phone}`,
      );

      if (data?.user.email) {
        await queryClient.invalidateQueries({
          queryKey: notificationsKeys.user(data?.user.email),
        });
      }

      onConnect();
      showToast(`SMS has been was updated`, ToastTypeEnum.SUCCESS);
      setLoading(false);
      onClose();
    }
  };

  return (
    <Modal isOpen={openModal} onOpenChange={onClose}>
      <div className="p-8">
        <div className="flex justify-between mb-3">
          <span className="font-semibold pb-3 text-xl text-text-primary">
            SMS configuration
          </span>
          <X
            className="text-text-quaternary cursor-pointer"
            onClick={onClose}
          />
        </div>
        <p className="text-text-primary mb-4">
          Set the phone number you want to receive SMS notifications.
        </p>
        <PhoneInput
          onPhoneChange={(number) => setPhone(number)}
          onPrefixChange={(p) => setPrefix(p)}
          phoneValue={phone}
          selectedPrefix={prefix}
        />
        <div className="flex mt-4 justify-end">
          <Button onPress={onClose} variant="secondary">
            Cancel
          </Button>
          <Button
            className="flex flex-row items-center mt-auto ml-2 w-25 mr-2"
            isDisabled={loading}
            onPress={handleAddSMS}
          >
            {loading ? <Loading /> : "Connect"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
